import { useState, useEffect, useMemo } from 'react';
import {
  Mail,
  Phone,
  Building,
  Globe,
  Trash2,
  Download,
  Eye,
  FolderKanban,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INITIATIVE_META = {
  hpc: {
    label: 'HPC',
    badgeClass: 'bg-[#F15A24] text-white',
  },
  seeal: {
    label: 'SEEAL Africa',
    badgeClass: 'bg-[#0B5FA5] text-white',
  },
  rahvai: {
    label: 'RAHVAI',
    badgeClass: 'bg-[#2D3E55] text-white',
  },
  unknown: {
    label: 'Unknown',
    badgeClass: 'bg-slate-500 text-white',
  },
};

function getInitiativeMeta(initiative) {
  return INITIATIVE_META[initiative] || INITIATIVE_META.unknown;
}

function normalizePartnershipRecord(item) {
  const initiative = item.initiative || item.source_site || 'hpc';
  const initiativeMeta = getInitiativeMeta(initiative);

  return {
    ...item,
    initiative,
    initiative_name:
      item.initiative_name || item.source_label || initiativeMeta.label,
    source_site: item.source_site || initiative,
    source_label:
      item.source_label || item.initiative_name || initiativeMeta.label,
  };
}

export default function AdminPartnerships() {
  const { token } = useAuth();

  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedPartnership, setSelectedPartnership] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [initiativeFilter, setInitiativeFilter] = useState('all');

  useEffect(() => {
    fetchPartnerships();
  }, [page, initiativeFilter]);

  const fetchPartnerships = async () => {
    try {
      setLoading(true);

      const params = { page, limit: 20 };
      if (initiativeFilter !== 'all') {
        params.initiative = initiativeFilter;
      }

      const response = await axios.get(`${API_URL}/api/admin/partnerships`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      const items = Array.isArray(response.data.items)
        ? response.data.items.map(normalizePartnershipRecord)
        : [];

      setPartnerships(items);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch partnerships');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (partnershipId) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/partnerships/${partnershipId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPartnerships((prev) =>
        prev.map((item) =>
          item.id === partnershipId ? { ...item, is_read: true } : item
        )
      );

      if (selectedPartnership?.id === partnershipId) {
        setSelectedPartnership((prev) =>
          prev ? { ...prev, is_read: true } : prev
        );
      }
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await axios.delete(`${API_URL}/api/admin/partnerships/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Partnership inquiry deleted');
      setDeleteId(null);

      if (selectedPartnership?.id === deleteId) {
        setSelectedPartnership(null);
      }

      fetchPartnerships();
    } catch (error) {
      toast.error('Failed to delete partnership');
    }
  };

  const handleViewPartnership = (partnership) => {
    const normalized = normalizePartnershipRecord(partnership);
    setSelectedPartnership(normalized);

    if (!normalized.is_read) {
      handleMarkRead(normalized.id);
    }
  };

  const handleExport = () => {
    const exportUrl =
      initiativeFilter === 'all'
        ? `${API_URL}/api/admin/export/partnerships`
        : `${API_URL}/api/admin/export/partnerships?initiative=${encodeURIComponent(
            initiativeFilter
          )}`;

    window.open(exportUrl, '_blank');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';

    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filterLabel = useMemo(() => {
    if (initiativeFilter === 'all') return 'All initiatives';
    return getInitiativeMeta(initiativeFilter).label;
  }, [initiativeFilter]);

  return (
    <AdminLayout>
      <div data-testid="admin-partnerships">
        {/* Header */}
        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#2D3E55]">
              Partnership Inquiries
            </h1>
            <p className="font-body text-slate-600 mt-1">
              View and manage centralized partnership requests across HPC, SEEAL, and RAHVAI
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full xl:w-auto">
            <div className="w-full sm:w-[220px]">
              <Select
                value={initiativeFilter}
                onValueChange={(value) => {
                  setInitiativeFilter(value);
                  setPage(1);
                }}
              >
                <SelectTrigger data-testid="initiative-filter">
                  <div className="flex items-center gap-2">
                    <Filter size={16} />
                    <SelectValue placeholder="Filter by initiative" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Initiatives</SelectItem>
                  <SelectItem value="hpc">HPC</SelectItem>
                  <SelectItem value="seeal">SEEAL Africa</SelectItem>
                  <SelectItem value="rahvai">RAHVAI</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleExport}
              variant="outline"
              data-testid="export-csv-btn"
            >
              <Download size={18} className="mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Active Filter */}
        <div className="mb-6">
          <Badge variant="outline" className="px-3 py-1 text-sm">
            Showing: {filterLabel}
          </Badge>
        </div>

        {/* Partnerships List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 bg-slate-200 rounded w-1/3 mb-4"></div>
                  <div className="h-4 bg-slate-200 rounded w-2/3 mb-4"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/4"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : partnerships.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="font-body text-slate-500">
                No partnership inquiries found for this filter
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {partnerships.map((partnership) => {
              const initiativeMeta = getInitiativeMeta(partnership.initiative);

              return (
                <Card
                  key={partnership.id}
                  className={`hover:shadow-md transition-shadow cursor-pointer ${
                    !partnership.is_read ? 'border-l-4 border-l-[#F7B500]' : ''
                  }`}
                  onClick={() => handleViewPartnership(partnership)}
                  data-testid={`partnership-${partnership.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <span className="font-body font-semibold text-[#2D3E55]">
                            {partnership.organization_name}
                          </span>

                          {!partnership.is_read && (
                            <Badge className="bg-[#F7B500] text-black">New</Badge>
                          )}

                          <Badge className={initiativeMeta.badgeClass}>
                            {initiativeMeta.label}
                          </Badge>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          <Badge variant="outline">
                            {partnership.organization_type}
                          </Badge>
                          <Badge className="bg-[#6EC1E4] text-[#113548]">
                            {partnership.partnership_interest}
                          </Badge>
                        </div>

                        <p className="font-body text-slate-600 text-sm line-clamp-2 mb-3">
                          {partnership.message}
                        </p>

                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Building size={14} />
                            {partnership.contact_person}
                          </span>

                          <span className="flex items-center gap-1">
                            <Globe size={14} />
                            {partnership.country}
                          </span>

                          <span className="flex items-center gap-1">
                            <FolderKanban size={14} />
                            {partnership.source_label || partnership.initiative_name}
                          </span>

                          <span>{formatDate(partnership.created_at)}</span>
                        </div>
                      </div>

                      <div
                        className="flex items-center gap-2 flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewPartnership(partnership);
                          }}
                        >
                          <Eye size={16} className="mr-1" />
                          View
                        </Button>

                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(partnership.id);
                          }}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>

            <span className="flex items-center px-4 font-body text-slate-600">
              Page {page} of {totalPages}
            </span>

            <Button
              variant="outline"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        )}

        {/* View Partnership Modal */}
        <Dialog
          open={!!selectedPartnership}
          onOpenChange={() => setSelectedPartnership(null)}
        >
          <DialogContent className="max-w-2xl">
            {selectedPartnership && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl text-[#2D3E55] flex flex-wrap items-center gap-2">
                    <span>{selectedPartnership.organization_name}</span>
                    <Badge
                      className={
                        getInitiativeMeta(selectedPartnership.initiative).badgeClass
                      }
                    >
                      {getInitiativeMeta(selectedPartnership.initiative).label}
                    </Badge>
                  </DialogTitle>

                  <DialogDescription>
                    Partnership inquiry received{' '}
                    {formatDate(selectedPartnership.created_at)}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-body text-xs text-slate-500 uppercase mb-1">
                        Contact Person
                      </p>
                      <p className="font-body font-medium">
                        {selectedPartnership.contact_person}
                      </p>
                    </div>

                    <div>
                      <p className="font-body text-xs text-slate-500 uppercase mb-1">
                        Country
                      </p>
                      <p className="font-body font-medium">
                        {selectedPartnership.country}
                      </p>
                    </div>

                    <div>
                      <p className="font-body text-xs text-slate-500 uppercase mb-1">
                        Organization Type
                      </p>
                      <Badge variant="outline">
                        {selectedPartnership.organization_type}
                      </Badge>
                    </div>

                    <div>
                      <p className="font-body text-xs text-slate-500 uppercase mb-1">
                        Partnership Interest
                      </p>
                      <Badge className="bg-[#6EC1E4] text-[#113548]">
                        {selectedPartnership.partnership_interest}
                      </Badge>
                    </div>

                    <div>
                      <p className="font-body text-xs text-slate-500 uppercase mb-1">
                        Initiative
                      </p>
                      <p className="font-body font-medium">
                        {selectedPartnership.initiative_name}
                      </p>
                    </div>

                    <div>
                      <p className="font-body text-xs text-slate-500 uppercase mb-1">
                        Source Site
                      </p>
                      <p className="font-body font-medium">
                        {selectedPartnership.source_site}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm border-t pt-4">
                    <a
                      href={`mailto:${selectedPartnership.email}`}
                      className="flex items-center gap-2 text-[#F15A24] hover:underline"
                    >
                      <Mail size={16} />
                      {selectedPartnership.email}
                    </a>

                    {selectedPartnership.phone && (
                      <a
                        href={`tel:${selectedPartnership.phone}`}
                        className="flex items-center gap-2 text-[#2D3E55] hover:underline"
                      >
                        <Phone size={16} />
                        {selectedPartnership.phone}
                      </a>
                    )}
                  </div>

                  <div>
                    <p className="font-body text-xs text-slate-500 uppercase mb-2">
                      Message
                    </p>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="font-body text-slate-700 whitespace-pre-wrap">
                        {selectedPartnership.message}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Partnership Inquiry</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this partnership inquiry? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}